import * as Core from '@skylab-shared/core';
import { RootContextBase } from '@skylab-shared/core';
import {
    DiscountKind,
    FiscalDeviceModel,
    SaleAdvanceDocumentItemModel,
    SaleDocumentItemModel, SaleDocumentKind,
    TinKind
} from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, isString } from '@skylab-shared/utils';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { PaymentMethodItem } from '../../payment-method/queries/payment-method-base.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';
import { isAdvance } from '../../sale/utils/sale-utils';
import { UnitItem, UnitQuery } from '../../unit/queries/unit.query-base';
import { VatRateForSaleQuery } from '../../vat/queries/vat-rate-for-sale.query-base';
import { VatRateItem } from '../../vat/queries/vat-rate-item';
import { DocumentToFiscalize } from '../common/suz-helper-types';
import { SuzInternalStatus } from '../common/suz-internal-status';

type SuzResponse<TData = any> = {
    status: SuzInternalStatus;
    suzMessages?: string[],
    data?: TData;
};

// VAT
enum SuzVatRateType {
    Numeric = 0,
    Exempt = 1,
    Inactive = 2,
}
interface SuzVatRate {
    Value: number;
    VatCode: number | string;
    VatRateType: SuzVatRateType;
}

// DISCOUNT
interface IDiscount {
    Description: string;
    Type: number;
    Value: number;
}

enum DiscountType {
    Amount = 0,
    Percent = 1,
}

// DEVICE
enum DeviceType {
    CashRegister = 0,
    FiscalPrinter = 1,
    BarcodeReader = 2,
    WeighingScale = 3,
    PriceChecker = 4,
    DataCollector = 5,
    LabelingScale = 6,
    PaymentTerminal = 7,
}

// RESPONSE

type SuzErrorType =  // not all
    | 'OperationError.DeviceArticleError'
    | 'OperationError.DailyReportRequired'
    | 'OperationError.OperationNotExecuted'
    | 'OperationError.NullArgument'
    | 'OperationError.DeviceError'
    | 'OperationError.NoConnection'
    | 'OperationError.PaymentTermValidation'
    | 'OperationError.OperationNotExecuted'
    | 'ServiceError.SampleError'
    | 'ServiceError.InvalidModelError'
    | 'ServiceError.SuzExceptionError'
    | 'ServiceError.SuzAccessToOperationDeniedExceptionError'
    | 'ServiceError.SuzIncorrectParametersExceptionError'
    | 'ServiceError.ExceptionError'
    | 'ServiceError.SuzUnknownExceptionError';

interface SuzOperationResponseValidation {
    $type?: SuzErrorType;
    DetailedComment?: string;
    GeneralComment?: string;
    ParameterName?: string;
    RunningOperationInfo?: {
        ClientName?: string;
        StartTime?: string;
        OperationName?: string;
        DeviceName?: string;
    };
}

interface SuzOperationResponse {
    $type?: SuzErrorType;
    Cancelled: boolean;
    Data?: {
        DocumentTicket: {
            Date: string;
            Number: string;
        };
        FiscalDeviceTicket: {
            SerialNumber: string;
        };
    };
    Errors?: SuzOperationResponseValidation[];
    Warnings?: SuzOperationResponseValidation[];

    ErrorMessage?: string;
    ErrorType?: string;
}

// RECEIPT
interface IOperation {
    $type: string;
    Args: IDefaultData;
}

interface IDefaultData {
    BusinessParamsWithValues: {};
}

interface IReceipt extends IDefaultData {
    Receipt: {
        Articles: IArticleTemplate[];
        BuyerTaxId: any;
        CustomFields: ICustomFields;
        Discount: IDiscount;
        ExtraData: {
            Cashier: string;
            CRNumber: string;
            FooterLines: any[];
        };
        InvoiceData: any;
        Packages: IPackageTemplate[];
        PaymentTerms: {};
        SystemNumber: any;
    };
}

interface IArticleTemplate {
    CustomFields: ICustomFields;
    Discount: IDiscount;
    IsVoid: boolean;
    Name: string;
    Price: number;
    Quantity: number;
    Unit: any;
    Value: any;
    Vat: SuzVatRate;
}

interface IPackageTemplate {
    CustomFields: ICustomFields;
    IsVoid: boolean;
    Name: string;
    Price: number;
    Quantity: number;
    Unit: any;
    Value: any;
}

interface ICustomFields {
    Description?: string | null;
}

interface DeviceParameters {
    discount: boolean;
    description: boolean;
    buyerTaxId: boolean;
}

// SERVICE
export class Suz17ServiceBase {
    // dependencies
    private localContext: Core.LocalContext;
    private logger = new Core.ConsoleLogger(this.constructor.name);
    private axiosInstance: AxiosInstance;
    // other
    private defaultData!: IDefaultData;
    private receipt!: IReceipt;
    private articleTemplate!: IArticleTemplate;
    public readonly suzBaseUrl = 'https://localhost:34412/InsERT/SUZ/';

    public constructor(
        private readonly l10n: Core.L10nBase,
        private readonly rootContext: RootContextBase,
    ) {
        this.localContext = this.rootContext.createLocalContext();
        this.axiosInstance = axios.create({
            baseURL: '',
            timeout: 60000,
            headers: {
                'Content-Type': 'application/json;charset=UTF-8',
                'Access-Control-Allow-Origin': '*',
                'Cache-Control': 'must-revalidate, no-store, no-cache, private'
            }
        });
        this.prepareData();
        this.preloadData();
    }

    //#region preparing data templates
    private prepareData(): void {
        this.defaultData = {
            BusinessParamsWithValues: {},
        };
        this.receipt = {
            BusinessParamsWithValues: {},
            Receipt: {
                Articles: [],
                BuyerTaxId: null,
                CustomFields: {},
                Discount: null as any,
                ExtraData: null as any,
                InvoiceData: null,
                Packages: [],
                PaymentTerms: {},
                SystemNumber: null,
            },
        } as const;
        this.articleTemplate = {
            CustomFields: {},
            Discount: {
                Description: null as any,
                Type: 0,
                Value: 0,
            },
            IsVoid: false,
            Name: '',
            Price: 0,
            Quantity: 0,
            Unit: null,
            Value: null,
            Vat: {
                Value: 0,
                VatCode: 0,
                VatRateType: 0,
            },
        } as const;
    }
    //#endregion

    //#region data
    private async preloadData(): Promise<void> {
        await Promise.all([
            this.localContext.preload(UnitQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(PaymentMethodQuery),
        ]);
    }

    private getUnitSymbol(id: number): string {
        return this.units.find(u => u.id === id)?.symbol || '';
    }

    private get units(): UnitItem[] {
        return this.localContext.getPreloaded(UnitQuery) || [];
    }

    private get vatRates(): VatRateItem[] {
        return this.localContext.getPreloaded(VatRateForSaleQuery) || [];
    }

    private get paymentMethods(): PaymentMethodItem[] {
        return this.localContext.getPreloaded(PaymentMethodQuery) || [];
    }
    //#endregion

    //#region break action handler
    //#endregion

    //#region data setters
    private setArticles(
        currentReceipt: IReceipt,
        document: DocumentToFiscalize,
        productsNames: { id: number; name: string }[],
        params: DeviceParameters,
        printer: FiscalDeviceModel,
        errorCallback: (err: any) => void): void {
        document.items.forEach(item => {
            const article = structuredClone(this.articleTemplate);
            const articleVat = this.setVatRate(document.kind, item.vatRateId, printer, errorCallback);
            if (typeof articleVat === 'undefined') return;
            article.Vat = articleVat;

            const isAdvanceInvoice = isAdvance(document);
            const product = productsNames.find(p => p.id === item.productId);
            article.Name = product ? product.name : item.productName || '';
            // dla fakury zaliczkowej dodajemy prefix Zaliczka
            if (isAdvanceInvoice) {
                article.Name = 'Zaliczka ' + article.Name;
            }
            article.Discount =  (isAdvanceInvoice ? null : this.setDiscount(item)) as any;
            article.IsVoid = false;
            // dla fakury zaliczkowej uzywamy bezwzględnie 1 przy ilosci
            article.Quantity = isAdvanceInvoice ? 1 : item.quantity;
            article.Price = isAdvanceInvoice ? (<SaleAdvanceDocumentItemModel>item).advance : item.price_grossBeforeDiscount;
            article.Value = null;
            article.Unit = this.getUnitSymbol(item.unitId);

            if (params.description) {
                article.CustomFields.Description = item.note;
            }

            currentReceipt.Receipt.Articles.push(article);
        });
    }

    private setVatRate(documentKind: SaleDocumentKind, vatRateId: number, device: FiscalDeviceModel, errorCallback: Function): SuzVatRate | undefined {
        // dla faktury marzy uzywamy bezwzględnie skonfigurowanego zera technicznego
        if (documentKind === SaleDocumentKind.VatMarginInvoice) {
            const technicalZero = device?.vatRatesParameters?.technicalZero;
            if (technicalZero) {
                return {
                    Value: 0,
                    VatCode: technicalZero,
                    VatRateType: SuzVatRateType.Numeric
                };
            }
            errorCallback({ status: SuzInternalStatus.MappingError, suzMessages: [this.l10n.instant('business.suz.messages.vat-rate-technical-zero-mapping-error')] });
            return undefined;

        } else if (device?.vatRatesParameters?.mappings) {
            const mapping = device.vatRatesParameters.mappings.filter(m => m.vatRateId === vatRateId).shift();
            if (mapping != null) {
                const vatRate = this.getVatRateInfo(vatRateId);
                return {
                    Value: vatRate.vatValue ?? 0,
                    VatCode: mapping.mapped,
                    VatRateType: vatRate.vatType
                };
            }
            errorCallback({ status: SuzInternalStatus.MappingError, suzMessages: [this.l10n.instant('business.suz.messages.vat-rate-mapping-error')] });
            return undefined;

        } else {
            const vatRate = this.getVatRateInfo(vatRateId);
            return {
                Value: vatRate.vatValue ?? 0,
                VatCode: 0,
                VatRateType: vatRate.vatType,
            };
        }
    }

    private getVatRateInfo(id: number): { vatValue: number | null; vatType: SuzVatRateType; } {
        const vatRate = this.vatRates.find(v => v.id === id);
        if (!vatRate) {
            return { vatType: SuzVatRateType.Inactive, vatValue: null };
        }
        const vatType = this.getVatRateType(vatRate);
        const vatValue = vatType === SuzVatRateType.Numeric ? vatRate.rate : null;
        return {
            vatType,
            vatValue
        };
    }

    private getVatRateType(vatRate: VatRateItem): SuzVatRateType {
        if (vatRate.untaxed || vatRate.exempt) {
            return SuzVatRateType.Exempt;
        } else if (vatRate.rate >= 0) {
            return SuzVatRateType.Numeric;
        } else {
            return SuzVatRateType.Inactive;
        }
    }

    private setDiscount(item: SaleDocumentItemModel): IDiscount | null {
        const discount: any = {
            Type: 0,
            Value: 0,
            Description: null
        };
        if (item.discountValue !== 0) {
            discount.Type = DiscountType.Amount;
            discount.Value = Calc.round(item.value_grossAfterDiscount - item.value_grossBeforeDiscount, 2, MidpointRounding.AwayFromZero);
            discount.Description = item.discountKind === DiscountKind.Percentage ? (item.discountValue * 100).toFixed(2).toString().replace('.', ',') + '%' : null;

            if (discount.Value === 0) return null; // jeśli cena rabatu zaokrąglona wynosi 0
            return discount as IDiscount;
        } else {
            return null;
        }
    }

    private async setPaymentFormAsync(currentReceipt: IReceipt, document: DocumentToFiscalize, printer: FiscalDeviceModel, errorCallback: (err: any) => void): Promise<void> {
        const paymentMethod = this.paymentMethods.find(p => p.id === document.paymentMethodId);
        if (paymentMethod) {
            const price = isAdvance(document) ? document.advanceValue : document.grossValue;

            if (printer?.paymentMethodsParameters?.mappings?.length) {
                const mapping = printer.paymentMethodsParameters.mappings.find(m => m.paymentMethodId === document.paymentMethodId);
                if (mapping) {
                    (currentReceipt.Receipt.PaymentTerms as any)[mapping.mapped] = price;
                } else {
                    errorCallback({ status: SuzInternalStatus.MappingError, suzMessages: [this.l10n.instant('business.suz.messages.payment-method-mapping-error')] });
                }
            } else {
                (currentReceipt.Receipt.PaymentTerms as any)[paymentMethod.name] = price;
            }
        }
    }
    //#endregion

    //#region suz getters

    public async checkConnectionAsync(): Promise<SuzResponse> {
        try {
            const response = await this.axiosInstance.get<string[]>(this.getSuzUrl('api/devices?namesOnly=true'));
            return { status: SuzInternalStatus.Success, data: response.data };
        } catch (err) {
            return {
                status: SuzInternalStatus.Error,
            };
        }
    }

    private async getDevicesAsync(): Promise<SuzResponse> {
        // any: SUZ DEVICES WITH DETAILS LIST
        try {
            const response = await this.axiosInstance.get<string[]>(this.getSuzUrl('api/devices'));
            return { status: SuzInternalStatus.Success, data: response.data };
        } catch (err) {
            return { status: SuzInternalStatus.Error };
        }
    }

    private async getDeviceDetailsAsync(deviceName: string): Promise<SuzResponse> {
        // any: SUZ DEVICE SETTINGS
        try {
            const response = await this.axiosInstance.get(this.getSuzUrl('api/devices/', deviceName));
            return { status: SuzInternalStatus.Success, data: response.data };
        } catch (err) {
            return {
                status: SuzInternalStatus.Error
            };
        }
    }

    private async getDetailsForModelAsync(modelName: string): Promise<SuzResponse> {
        // any: SUZ MODEL DRIVER SETTING
        try {
            const response = await this.axiosInstance.get(this.getSuzUrl('api/models/', modelName));
            return { status: SuzInternalStatus.Success, data: response.data };
        } catch (err) {
            return { status: SuzInternalStatus.Error };
        }
    }

    public async getPrinterNamesAsync(): Promise<SuzResponse<string[]>> {
        const devicesNames: string[] = [];
        try {
            const response = await this.getDevicesAsync();
            const devices: any[] = response.data || [];
            if (devices.length) {
                const models: string[] = [];
                devices.forEach(device => {
                    if (models.indexOf(device.ModelName) === this.SuzNoConnectionStatus) {
                        models.push(device.ModelName);
                    }
                });
                const modelRequests: Promise<SuzResponse>[] = [];
                models.forEach(model => {
                    modelRequests.push(this.getDetailsForModelAsync(model));
                });

                const modelResponses = await Promise.all(modelRequests);
                for (const device of devices) {
                    for (let i = 0; i < modelResponses.length; i++) {
                        if (device.ModelName === models[i]) {
                            if (modelResponses[i].data.ModelTicket.DeviceType === DeviceType.FiscalPrinter) {
                                devicesNames.push(device.DeviceName);
                                i = modelResponses.length;
                            }
                        }
                    }
                }
            }
            response.data = devicesNames;
            return response;
        } catch (err) {
            return {
                status: SuzInternalStatus.Error,
            };
        }
    }
    //#endregion

    //#region error parsing
    private getAllErrorKeyMessages(errorObj: any, errorKey: string): string {
        let value = '';
        const getKeyValue = (obj: any) => {
            for (const key in obj) {
                if (key === errorKey && obj[errorKey]) {
                    value += this.addDotAtTheEnd(obj[errorKey]);
                } else if (obj[key] && typeof obj[key] === 'object') {
                    getKeyValue(obj[key]);
                }
            }
        };
        getKeyValue(errorObj);
        return value;
    }

    private addDotAtTheEnd(text: string): string {
        return text.concat(text.endsWith('.') ? ' ' : '. ');
    }

    private addDefaultMessages(text: string, error: any): string {
        if (error.GeneralComment) {
            text += this.addDotAtTheEnd(error.GeneralComment);
        }
        if (error.DetailedComment) {
            text += this.addDotAtTheEnd(error.DetailedComment);
        }

        return text;
    }

    private parseTextFromOperationResult(operationResults: SuzOperationResponse): SuzResponse {
        const suzMessages: string[] = [];
        const response: SuzResponse = {
            suzMessages,
            data: operationResults.Data,
            status: SuzInternalStatus.Success,
        };

        if (operationResults.Errors) {
            for (const error of operationResults.Errors) {
                suzMessages.push(this.addDefaultMessages('', error));
                response.status = SuzInternalStatus.Error;
                switch (
                    error.$type // SuzErrorType
                ) {
                    case 'OperationError.OperationNotExecuted':
                        response.status = SuzInternalStatus.OperationNotExecuted;
                        break;
                    case 'OperationError.DeviceArticleError':
                        suzMessages.push(this.addDotAtTheEnd(this.l10n.instant('business.suz.messages.invalid-vat-rate-on-position')));
                        break;
                    case 'OperationError.DailyReportRequired':
                        response.status = SuzInternalStatus.DailyReportRequired;
                        break;
                    case 'OperationError.NoConnection':
                        response.status = SuzInternalStatus.DeviceConnectionError;
                        break;
                    case 'ServiceError.SuzExceptionError':
                        suzMessages.push(this.addDotAtTheEnd(this.l10n.instant('business.suz.messages.device-not-exists-error')));
                        response.status = SuzInternalStatus.DeviceNotExistsError;
                        break;
                    case 'ServiceError.SuzAccessToOperationDeniedExceptionError':
                        const message = `${this.l10n.instant('business.suz.messages.device-is-busy')} ClientName: ${error.RunningOperationInfo?.ClientName}, StartTime: ${error.RunningOperationInfo?.StartTime}, OperationName: ${error.RunningOperationInfo?.OperationName}, DeviceName: ${error.RunningOperationInfo?.DeviceName}.`;
                        suzMessages.push(message);

                        break;
                    case 'ServiceError.InvalidModelError':
                        suzMessages.push(this.addDotAtTheEnd(this.l10n.instant('business.suz.messages.invalid-model-error')));
                        break;
                }
            }
        }

        if (operationResults && isString(operationResults.ErrorMessage)) {
            suzMessages.push(this.getAllErrorKeyMessages(operationResults, 'ErrorMessage'));

            response.status = SuzInternalStatus.Error;
            switch (operationResults.ErrorType) {
                case 'DeviceNotFoundException':
                    response.status = SuzInternalStatus.DeviceNotExistsError;
                    break;
            }
        }
        return response;
    }
    //#endregion

    //#region operations
    private async endPrinterOperationAsync(printerName: string): Promise<SuzResponse> {
        try {
            const response = await this.axiosInstance.get<SuzOperationResponse>(
                this.getSuzUrl('api/operations/', printerName, '/end'),
            );
            const operationResult = response.data;
            if (operationResult == null) {
                // run to get operation results
                return this.endPrinterOperationAsync(printerName);
            } else {
                return this.parseTextFromOperationResult(operationResult);
            }
        } catch (err) {
            const error: AxiosError<SuzOperationResponse> = err as AxiosError<SuzOperationResponse>;
            if (error?.response && error.response.data !== void 0 && error.response.status !== this.SuzNoConnectionStatus) {
                const operationResult = error.response.data;
                if (operationResult == null) {
                    /*
                        when error - still ask printer for operation results
                        lack of paper, paper stuck or any other problem which can be solved by manual action on printer
                    */
                    return this.endPrinterOperationAsync(printerName);
                } else {
                    return this.parseTextFromOperationResult(operationResult);
                }
            } else {
                return {
                    status: SuzInternalStatus.Error,
                    suzMessages: [this.l10n.instant('business.suz.messages.suz-connection-error')]
                };
            }
        }
    }

    private async printerOperationAsync(operation: IOperation, printerName: string): Promise<SuzResponse> {
        try {
            // trigger the mechanizm
            await this.axiosInstance.post<SuzResponse>(
                this.getSuzUrl('api/operations/', printerName, '/begin'),
                operation,
            );
        } catch (err) {
            const error: AxiosError<SuzOperationResponse> = err as AxiosError<SuzOperationResponse>;
            if (error?.response && error.response.data !== void 0 && error.response.status !== this.SuzNoConnectionStatus) {
                const operationResult = error.response.data;
                if (operationResult == null) {
                    /*
                        when error - still ask printer for operation results
                        lack of paper, paper stuck or any other problem which can be solved by manual action on printer
                    */
                    return this.endPrinterOperationAsync(printerName);
                } else {
                    return this.parseTextFromOperationResult(operationResult);
                }
            } else {
                return {
                    status: SuzInternalStatus.DeviceConnectionError,
                    suzMessages: [this.l10n.instant('business.suz.messages.suz-connection-error')]
                };
            }
        }

        try {
            // check if mechanizm is ended
            return await this.endPrinterOperationAsync(printerName);
        } catch (err) {
            this.logger.error(err);
            return {
                status: SuzInternalStatus.Error
            };
        }
    }
    //#endregion

    //#region printer operations
    public async checkPrinterConnectionAsync(printerName: string): Promise<SuzResponse> {
        try {
            const operation = {
                $type: 'PassiveDevice.CheckConnection',
                Args: {
                    BusinessParamsWithValues: {},
                },
            };
            const response = await this.printerOperationAsync(operation, printerName);
            return response;
        } catch (response) {
            return response as any;
        }
    }

    private getReceiptAdditionalParamsAsync(deviceDetails: any): DeviceParameters {
        try { // device details
            const valueMap: Record<string, boolean> = { false: false, true: true };

            const discount = deviceDetails.BusinessParametersValues.PrintItemDiscount;
            const description = deviceDetails.BusinessParametersValues.PrintItemDescription;
            const buyerTaxId = deviceDetails.BusinessParametersValues.PrintBuyerTaxId;
            return {
                discount: discount && valueMap[discount.toLowerCase()] || true,  // rabat pokazywany
                description: description && valueMap[description.toLowerCase()] || false, // dodatkowy opis pozycji
                buyerTaxId: buyerTaxId && valueMap[buyerTaxId.toLowerCase()] || false, // widoczność nip
            };
        } catch (err) {
            this.logger.error(err);
            return {
                discount: true,
                description: false,
                buyerTaxId: false
            };
        }
    }

    private setReceiptAdditionalParams(document: DocumentToFiscalize, currentReceipt: IReceipt, deviceDetails: any): void {
        const businessParams = deviceDetails.BusinessParametersValues;
        if (businessParams.PrintSystemNumber) { // numer systemowy
            currentReceipt.Receipt.SystemNumber = document.documentNumber;
        }

        if (businessParams.PrintCashierName) { // nazwa kasjera
            currentReceipt.Receipt.ExtraData = {
                Cashier: document.issuer,
                CRNumber: '  ',
                FooterLines: []
            };
        }
    }

    public async printReceiptAsync(document: DocumentToFiscalize, productsNames: { id: number; name: string }[], printer: FiscalDeviceModel): Promise<SuzResponse> {
        const currentReceipt = structuredClone(this.receipt);
        const deviceDetails = (await this.getDeviceDetailsAsync(printer.name)).data; //cheking if buisness parameters exists
        let params = <DeviceParameters>{};

        if (deviceDetails) {
            this.setReceiptAdditionalParams(document, currentReceipt, deviceDetails);
            params = this.getReceiptAdditionalParamsAsync(deviceDetails);
        }

        let lastError: SuzResponse | undefined = undefined;
        try { // fullfill needed receipit params
            if (params.buyerTaxId) {
                if (document.buyer) {
                    if (document.buyer.tinKind === TinKind.NIP) {
                        currentReceipt.Receipt.BuyerTaxId = {
                            Value: document.buyer.tin || '',
                            Type: 0
                        };
                    }
                } else if (document.client && document.client.tinKind === TinKind.NIP) {
                    currentReceipt.Receipt.BuyerTaxId = {
                        Value: document.client.tin || '',
                        Type: 0
                    };
                }
            }

            this.setArticles(
                currentReceipt,
                document,
                productsNames,
                params,
                printer,
                (newError: any) => {
                    lastError = newError;
                });
            await this.setPaymentFormAsync(
                currentReceipt,
                document,
                printer,
                (newError: any) => {
                    lastError = newError;
                });

        } catch (err) {
            this.logger.error(err);
        }

        if (!lastError) {
            let response: SuzResponse;
            const operation = {
                $type: 'FiscalPrinter.PrintReceipt',
                Args: currentReceipt
            };
            try {
                response = await this.printerOperationAsync(operation, printer.name);
                return response;
            } catch (err) {
                this.logger.error(err);
                return {
                    status: SuzInternalStatus.Error,
                    suzMessages: [this.l10n.instant('business.suz.messages.suz-connection-error')]
                };
            }

        } else {
            return lastError;
        }
    }

    public async createDailyReportAsync(printerName: string): Promise<SuzResponse> {
        const operation = {
            $type: 'FiscalPrinter.PrintDailyReport',
            Args: this.defaultData,
        };
        try {
            const response = await this.printerOperationAsync(operation, printerName);
            return response;
        } catch (err) {
            this.logger.error(err);
            return {
                status: SuzInternalStatus.Error,
                suzMessages: [err instanceof Error ? err.message : String(err)]
            };
        }
    }

    public async getDeviceVatTableAsync(printerName: string): Promise<SuzResponse> {
        const operation = {
            $type: 'PassiveDevice.ReadVatTable',
            Args: this.defaultData,
        };
        try {
            const response = await this.printerOperationAsync(operation, printerName);
            return response;
        } catch (err) {
            this.logger.error(err);
            return { status: SuzInternalStatus.Error };
        }
    }

    public async getDevicePaymentMethodsAsync(printerName: string): Promise<SuzResponse> {
        const operation = {
            $type: 'PassiveDevice.ReadPaymentTerms',
            Args: this.defaultData,
        };
        try {
            const response = await this.printerOperationAsync(operation, printerName);
            return response;
        } catch (err) {
            this.logger.error(err);
            return { status: SuzInternalStatus.Error };
        }
    }
    //#endregion

    // #region

    private getSuzUrl(method: string, printerParam = '', methodType = ''): string {
        const preparedPrinterParam = printerParam
            .replaceAll('+', '%252B')
            .replaceAll('/', '%25252F')
            .replaceAll('#', '%2523')
            .replaceAll('?', '%253F');

        return this.suzBaseUrl + method + preparedPrinterParam + methodType;
    }

    private readonly SuzNoConnectionStatus = -1;

    // #endregion
}
